import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, useTheme, useMediaQuery, Button, CircularProgress, Alert, Chip,
} from "@mui/material";
import {
  Groups as GroupsIcon,
  BookmarkAdded as BookmarkAddedIcon,
  ErrorOutline as ErrorOutlineIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { uploadRunningFit, getRunningPrediction, predictDemocratic, getRunningProfile } from "../../api/runningApi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../../contexts/AuthContext";
import { clearRunningConfigCache } from "../../services/runningConfigService";
import RunningFileUpload from "../../components/running/RunningFileUpload";
import CourseSelectorDrawer from "../../components/running/CourseSelectorDrawer";
import RacePredictionHelp from "../../components/running/RacePredictionHelp";
import { apiErrorMessage } from "../../utils/apiError";

const RunningDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mode 3 — democratic prediction over the user's saved secondary reference files.
  // Opens the same running-analysis prediction page as every other prediction type.
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');

  // Whether multi-file prediction can run at all, checked BEFORE offering it.
  // Five of these in production came back as a flat 400 after the user had
  // already chosen a race — all from accounts in their first week, who had no
  // runs marked and no way to know that was the requirement.
  const { isAuthenticated } = useAuth();
  const [demoReady, setDemoReady] = useState(null);   // null = not checked yet
  const [fitFileCount, setFitFileCount] = useState(0);
  // The marked runs themselves, not just how many. Naming them here is the only
  // place outside the picker that shows what a prediction would actually be
  // built from — otherwise "all the FIT files you marked" is a promise the page
  // never backs up.
  const [secondaryFiles, setSecondaryFiles] = useState([]);
  // Bumped to ask RunningFileUpload to open My Files, where runs are marked.
  const [openFilesSignal, setOpenFilesSignal] = useState(0);
  // Every control here leads to the same place for the same reason, so they all
  // go through this: open the drawer and point at the bookmark column.
  const [markRunsSignal, setMarkRunsSignal] = useState(0);
  const openMyFilesToMark = () => {
    setOpenFilesSignal((n) => n + 1);
    setMarkRunsSignal((n) => n + 1);
  };

  const refreshDemoReady = useCallback(() => {
    if (!isAuthenticated) { setDemoReady(false); setSecondaryFiles([]); return; }
    getRunningProfile()
      .then((p) => {
        setFitFileCount(p?.fit_file_count ?? 0);
        setSecondaryFiles(Array.isArray(p?.files) ? p.files : []);
        setDemoReady((p?.usable_secondary_count ?? 0) > 0);
      })
      // Leave it enabled if the check itself fails — better to let the request
      // through and show its own error than to block a feature over a failed probe.
      .catch(() => setDemoReady(true));
  }, [isAuthenticated]);

  useEffect(() => { refreshDemoReady(); }, [refreshDemoReady]);

  const handleDemocraticSelect = async (source) => {
    setDemoOpen(false);
    setDemoError(''); setDemoLoading(true);
    try {
      const params = {};
      if (source.type === 'race') params.raceId = source.raceId;
      else if (source.type === 'myfile') params.courseFitFileId = source.fileId;
      else {
        setDemoError(t('demoPickRace', 'Pick a catalog race or one of your FIT files as the course.'));
        setDemoLoading(false); return;
      }
      const data = await predictDemocratic(params);
      navigate('/running/analysis', { state: { predictionData: data } });
    } catch (e) {
      setDemoError(apiErrorMessage(e, 'Democratic prediction failed'));
    } finally {
      setDemoLoading(false);
    }
  };

  const handlePredictionSelect = async (predictionId) => {
    setLoading(true);
    try {
      const data = await getRunningPrediction(predictionId);
      navigate('/running/analysis', {
        state: { predictionData: data }
      });
    } catch (error) {
      console.error('Load prediction error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, params) => {
    setLoading(true);
    try {
      const response = await uploadRunningFit(file, params);

      // Clear config cache so next load fetches updated values from backend
      clearRunningConfigCache();
      console.log('Running configuration cache cleared after upload');

      // Navigate to analysis page with the data, file, and reusedFileId for reanalysis
      navigate('/running/analysis', {
        state: {
          workoutData: response,
          file: file,
          reusedFileId: params.reused_file_id,
          analysisParams: params
        }
      });
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Garmin sends the user back here after the consent screen. The outcome
  // arrives as a query parameter, which is cleared once it has been shown so a
  // refresh or a shared link does not replay the message.
  const [searchParams, setSearchParams] = useSearchParams();
  const [garminNotice, setGarminNotice] = useState(null);

  useEffect(() => {
    const outcome = searchParams.get('garmin');
    if (!outcome) return;
    const messages = {
      connected: { severity: 'success', text: t('garminConnected', 'Garmin Connect™ is linked. New runs and rides will appear in the Garmin tab as you record them.') },
      denied: { severity: 'info', text: t('garminDenied', 'Garmin connection cancelled — nothing was shared.') },
      expired: { severity: 'warning', text: t('garminExpired', 'That connection attempt timed out. Please try connecting again.') },
      error: { severity: 'error', text: t('garminError', 'Something went wrong connecting your Garmin account. Please try again.') },
    };
    setGarminNotice(messages[outcome] || messages.error);
    searchParams.delete('garmin');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, t]);

  return (
    <Box m={isMobile ? "10px" : "20px"} display="flex" justifyContent="center" sx={{ overflowX: 'hidden' }}>
      <Box maxWidth="1200px" width="100%">
        {garminNotice && (
          <Alert
            severity={garminNotice.severity}
            onClose={() => setGarminNotice(null)}
            sx={{ mb: 2 }}
          >
            {garminNotice.text}
          </Alert>
        )}
        {/* Welcome message - can be customized for running */}
        <Box
          sx={{
            backgroundColor: colors.grey[800],
            borderRadius: "8px",
            p: 3,
            mb: 3
          }}
        >
          <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={1}>
            {t('runningAnalysis', 'Running Analysis')}
          </Typography>
          <Typography variant="body1" color={colors.grey[300]}>
            {t('runningDashboardWelcomeText', 'Upload a FIT file and get instant breakdown of pace, grade-adjusted pace (GAP), heart rate zones, stamina, climbs, and descents. Select any race from the catalog to predict your finish time and get a segment-by-segment race plan with aid station stops. Compare two runs side by side — or watch them race each other on an animated map.')}
          </Typography>
        </Box>

        <Box mb={4} display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} justifyContent="space-between">
          <Box>
            <Typography
              variant="h2"
              fontWeight="bold"
              color={colors.grey[100]}
              mb={1}
            >
              {t('runningAnalysis', 'Running Analysis')}
            </Typography>
            <Typography variant="h5" color={colors.grey[300]}>
              {t('runningDashboardSubtitle', 'Upload your running FIT file to get started')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => navigate('/bike')}
            sx={{ whiteSpace: 'nowrap', mt: isMobile ? 2 : 0, ml: isMobile ? 0 : 2, backgroundColor: colors.blueAccent[600], '&:hover': { backgroundColor: colors.blueAccent[500] } }}
          >
            {t('switchToBiking', 'Switch to Biking →')}
          </Button>
        </Box>

        <RunningFileUpload
          onFileUpload={handleFileUpload}
          onPredictionSelect={handlePredictionSelect}
          loading={loading}
          // Same action as the Multi-file race prediction section below, offered
          // inside the file picker where the files are actually chosen.
          onMultiFilePredict={() => setDemoOpen(true)}
          openFilesSignal={openFilesSignal}
          markRunsSignal={markRunsSignal}
          // Marking happens inside that drawer, so re-check when it closes —
          // otherwise the section still reads "not set up" after the user has
          // just set it up.
          onFilesDrawerClose={refreshDemoReady}
        />

        {/* ── Mode 3: democratic multi-file race prediction (experimental v2) ── */}
        <Box mt={3} sx={{ backgroundColor: colors.grey[800], borderRadius: "8px", p: 2.5 }}>
          <Typography variant="h6" color={colors.grey[100]} fontWeight="bold"
            sx={{ mb: 0.5, display: 'inline-flex', alignItems: 'center' }}>
            {t('democraticTitle', 'Race prediction from several runs')}
            <RacePredictionHelp fontSize={16} color={colors.grey[300]} sx={{ ml: 0.5 }} />
          </Typography>
          <Typography variant="body2" color={colors.grey[300]} mb={1.5}>
            {t('democraticDesc', 'Predict any race using all the FIT files you marked for multi-file predictions in My Files. Each file makes its own prediction and they are averaged equally — every race has the same say.')}
          </Typography>
          {/* The runs this would actually use. A file marked but never analysed
              cannot feed a prediction, so it is shown greyed rather than left
              out — otherwise the user ticked something and nothing appeared. */}
          {secondaryFiles.length > 0 && (
            <Box mb={1.5}>
              <Typography variant="caption" color={colors.grey[400]} display="block" mb={0.75}>
                {t('democraticUsing', 'Using these runs ({{n}}/4):', { n: secondaryFiles.length })}
              </Typography>
              <Box
                display="flex"
                flexDirection={isMobile ? 'column' : 'row'}
                flexWrap={isMobile ? 'nowrap' : 'wrap'}
                alignItems={isMobile ? 'stretch' : 'center'}
                gap={0.75}
              >
                {secondaryFiles.map((f) => {
                  const dist = f.total_distance_km ? `${f.total_distance_km.toFixed(1)} km` : null;
                  const gain = f.elevation_gain_m ? `${f.elevation_gain_m} m D+` : null;
                  const detail = [dist, gain].filter(Boolean).join(' · ');
                  const skipped = t('democraticFileNotAnalyzed',
                    'Not analysed yet — this run will be skipped. Open it from My Files to analyse it.');
                  const Icon = f.analyzed ? BookmarkAddedIcon : ErrorOutlineIcon;
                  const iconColor = f.analyzed ? colors.greenAccent[400] : colors.grey[500];

                  /* On a phone a chip has to truncate, and it truncates from the
                     right — losing the distance, which is the part that tells
                     two runs apart. One row per run instead: the name gets the
                     ellipsis, the numbers always survive on their own line. */
                  if (isMobile) {
                    return (
                      <Box
                        key={f.id}
                        sx={{
                          display: 'flex', gap: 1, alignItems: 'flex-start',
                          border: `1px solid ${f.analyzed ? colors.greenAccent[600] : colors.grey[600]}`,
                          borderRadius: '6px', px: 1, py: 0.5, minWidth: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: '1rem', color: iconColor, mt: '2px', flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            color={f.analyzed ? colors.grey[100] : colors.grey[400]}
                          >
                            {f.filename}
                          </Typography>
                          {/* A tooltip cannot be reached by touch, so the reason
                              a marked run will be ignored is spelled out. */}
                          <Typography variant="caption" color={f.analyzed ? colors.grey[400] : colors.redAccent[300]} display="block">
                            {f.analyzed ? detail : skipped}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }
                  return (
                    <Chip
                      key={f.id}
                      size="small"
                      icon={<Icon />}
                      label={detail ? `${f.filename} — ${detail}` : f.filename}
                      title={f.analyzed
                        ? [f.filename, f.workout_date?.slice(0, 10), detail].filter(Boolean).join(' · ')
                        : skipped}
                      sx={{
                        maxWidth: '100%',
                        color: f.analyzed ? colors.grey[100] : colors.grey[400],
                        borderColor: f.analyzed ? colors.greenAccent[600] : colors.grey[600],
                        '& .MuiChip-icon': { color: iconColor, fontSize: '1rem' },
                      }}
                      variant="outlined"
                    />
                  );
                })}
                {/* The list answers "which runs", so the way to change it
                    belongs at the end of the list — not in the button row
                    below, where it reads as a second kind of prediction. */}
                {/* A 24px chip is under any sane touch target, so on a phone
                    the same action is a full-width button instead. */}
                {isMobile ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={openMyFilesToMark}
                    sx={{
                      mt: 0.25, textTransform: 'none',
                      color: colors.grey[200],
                      borderColor: colors.blueAccent[500],
                      borderStyle: 'dashed',
                      '&:hover': { borderColor: colors.blueAccent[400] },
                    }}
                  >
                    {t('democraticManageRuns', 'Mark runs in My Files')}
                  </Button>
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    clickable
                    icon={<EditIcon />}
                    label={t('democraticManageRuns', 'Mark runs in My Files')}
                    onClick={openMyFilesToMark}
                    sx={{
                      color: colors.grey[200],
                      borderColor: colors.blueAccent[500],
                      borderStyle: 'dashed',
                      '& .MuiChip-icon': { color: colors.blueAccent[400], fontSize: '0.95rem' },
                      '&:hover': { borderColor: colors.blueAccent[400], backgroundColor: colors.grey[700] },
                    }}
                  />
                )}
              </Box>
              {/* One run is a valid prediction, just the least reliable one. */}
              {secondaryFiles.length === 1 && (
                <Typography variant="caption" color={colors.grey[400]} display="block" mt={0.75}>
                  {t('democraticOneMarked',
                    'Mark a second run over a different distance for a steadier prediction.')}
                </Typography>
              )}
            </Box>
          )}
          {demoReady === false ? (
            /* Not set up yet. Say what is missing and offer the step that fixes
               it, rather than a button whose only outcome is an error. */
            <Box>
              <Typography variant="body2" color={colors.grey[300]} mb={1.5}>
                {!isAuthenticated
                  ? t('democraticNeedsLogin',
                      'Sign in to average several of your own runs into one prediction.')
                  : fitFileCount === 0
                    ? t('democraticNeedsFiles',
                        'This needs a few of your own runs to average. Upload a FIT file above to get started.')
                    /* Marked but none analysed — telling this user to go and
                       mark runs would be telling them to redo what they did. */
                    : secondaryFiles.length > 0
                      ? t('democraticNeedsAnalysis',
                          'The runs you marked have not been analysed yet, so there is nothing to predict from. Open each one from My Files to analyse it, then come back.')
                      : t('democraticNeedsMarking',
                          'Pick which of your runs to average: open My Files and tap the bookmark in the Multi-file column on two or more of them.')}
              </Typography>
              {/* With runs already marked, the chip row above carries the
                  same action — two buttons for one step would be worse than
                  none. */}
              {isAuthenticated && fitFileCount > 0 && secondaryFiles.length === 0 && (
                <Button
                  variant="outlined"
                  startIcon={<GroupsIcon />}
                  onClick={openMyFilesToMark}
                  sx={{ color: colors.grey[100], borderColor: colors.blueAccent[500], textTransform: 'none',
                        '&:hover': { borderColor: colors.blueAccent[400] } }}
                >
                  {t('democraticChooseFiles', 'Choose runs in My Files')}
                </Button>
              )}
            </Box>
          ) : (
            <Button
              variant="outlined"
              startIcon={demoLoading ? <CircularProgress size={16} /> : <GroupsIcon />}
              onClick={() => setDemoOpen(true)}
              disabled={demoLoading || demoReady === null}
              sx={{ color: colors.grey[100], borderColor: colors.blueAccent[500], textTransform: 'none',
                    '&:hover': { borderColor: colors.blueAccent[400] } }}
            >
              {demoLoading ? t('predicting', 'Predicting…') : t('selectRaceGpx', 'Select Race / GPX')}
            </Button>
          )}
          {demoError && <Alert severity="error" sx={{ mt: 1.5 }}>{demoError}</Alert>}
        </Box>
      </Box>

      {/* Race picker for democratic prediction → opens the analysis page */}
      <CourseSelectorDrawer
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        onSelect={handleDemocraticSelect}
      />
    </Box>
  );
};

export default RunningDashboard;
